import { Box } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { Plus } from '@sweep-io/sweep-design/dist/icons';
import { ActionsMenu, ActionsMenuAction } from '../common/actions-menu/ActionsMenu';
import FancyTag, { ColorVariant } from '../common/FancyTag';

export enum AgentInputMode {
  None = 'none',
  ExperimentalMode = 'experimentalMode',
  DeepDiveMode = 'deepDiveMode',
  BuildMode = 'buildMode',
}

interface AgentInputModeSelectorProps {
  selectedMode: AgentInputMode;
  onModeChange: (mode: AgentInputMode) => void;
}

const MODE_LABELS: Record<Exclude<AgentInputMode, AgentInputMode.None>, string> = {
  [AgentInputMode.ExperimentalMode]: 'Experimental Mode',
  [AgentInputMode.DeepDiveMode]: 'Deep Dive',
  [AgentInputMode.BuildMode]: 'Build Mode',
};

const COLOR_MAP: Record<AgentInputMode, ColorVariant> = {
  [AgentInputMode.None]: 'blue',
  [AgentInputMode.ExperimentalMode]: 'blue',
  [AgentInputMode.DeepDiveMode]: 'lilac',
  [AgentInputMode.BuildMode]: 'mint',
};

const AgentInputModeSelector = ({ selectedMode, onModeChange }: AgentInputModeSelectorProps) => {
  const actions: ActionsMenuAction<AgentInputMode>[] = [
    {
      label: (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body">Experimental Mode</Typography>
          <Typography variant="caption" color={colors.grey[700]}>
            Experimental mode for new features and ideas
          </Typography>
        </Box>
      ),
      value: AgentInputMode.ExperimentalMode,
      isSelected: selectedMode === AgentInputMode.ExperimentalMode,
    },
    {
      label: (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body">Deep Dive</Typography>
          <Typography variant="caption" color={colors.grey[700]}>
            Best for in-depth or complex discussions
          </Typography>
        </Box>
      ),
      value: AgentInputMode.DeepDiveMode,
      isSelected: selectedMode === AgentInputMode.DeepDiveMode,
    },
    {
      label: (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body">Build Mode</Typography>
          <Typography variant="caption" color={colors.grey[700]}>
            Create Flows, Email Templates, and Fields
          </Typography>
        </Box>
      ),
      value: AgentInputMode.BuildMode,
      isSelected: selectedMode === AgentInputMode.BuildMode,
    },
  ];

  const handleActionClick = (action: ActionsMenuAction<AgentInputMode>) => {
    // If the same mode is selected, toggle it off
    if (action.value === selectedMode) {
      onModeChange(AgentInputMode.None);
    } else {
      onModeChange(action.value);
    }
  };

  const handleTagDelete = () => {
    onModeChange(AgentInputMode.None);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <ActionsMenu
        actions={actions}
        onClick={handleActionClick}
        MenuButtonIcon={<Plus />}
        iconSize="small"
        iconVariant="flat"
        iconTooltip="Select advanced mode"
        menuWidth="300px"
      />
      {selectedMode !== AgentInputMode.None && (
        <FancyTag
          text={MODE_LABELS[selectedMode]}
          colorVariant={COLOR_MAP[selectedMode]}
          onDelete={handleTagDelete}
        />
      )}
    </Box>
  );
};

export default AgentInputModeSelector;
